import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../../components/layout/Layout";
import { PageHeader } from "../../components/layout/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Spinner } from "../../components/ui/Spinner";
import {
  listTeacherLessons,
  createCanDo,
  updateCanDo,
  deleteCanDo,
  generateCanDos,
  fetchCanDosBundle,
  createCommunicationTopic,
  updateCommunicationTopic,
  deleteCommunicationTopic,
  type LessonItem,
  type CanDoItem,
  type CanDoCreatePayload,
  type CommunicationTopicMeta,
} from "../../modules/tutor/api/tutor.client";
import {
  getTeacherMyClasses,
  getTeacherMySections,
  type TeacherClass,
} from "../../modules/roster/api/teacher.client";
import { COMMUNICATION_SKILLS_LESSON_NO, getCanDos } from "../../services/api/canDo";
import { ROUTES } from "../../utils/constants";
import { BookOpen, MessageCircle, Plus, Sparkles, Trash2, Pencil } from "lucide-react";

type TabKey = "lesson" | "communication";

type RubricRow = { level: number; label: string; criteria: string };

const DEFAULT_RUBRIC: RubricRow[] = [
  { level: 1, label: "Needs practice", criteria: "Student needs more practice" },
  { level: 2, label: "Developing", criteria: "Student is developing" },
  { level: 3, label: "Proficient", criteria: "Student is proficient" },
  { level: 4, label: "Excellent", criteria: "Student demonstrates excellence" },
];

function parseClassGrade(raw: string | number | null | undefined): number | null {
  if (raw == null || raw === "") return null;
  const n = typeof raw === "number" ? raw : parseInt(String(raw), 10);
  if (!Number.isFinite(n) || n < 1 || n > 12) return null;
  return n;
}

function uniqueGradesFromRoster(
  sectionItems: Awaited<ReturnType<typeof getTeacherMySections>>,
  classes: TeacherClass[]
): number[] {
  const fromSections = sectionItems
    .map((s) => parseClassGrade(s.class?.grade))
    .filter((n): n is number => n != null);
  const fromClasses = classes.map((c) => parseClassGrade(c.grade)).filter((n): n is number => n != null);
  return [...new Set([...fromSections, ...fromClasses])].sort((a, b) => a - b);
}

const FALLBACK_GRADES = Array.from({ length: 12 }, (_, i) => i + 1);

function normalizeRubric(r?: CanDoItem["rubric_levels"]): RubricRow[] {
  if (!r || r.length < 1) return DEFAULT_RUBRIC.map((x) => ({ ...x }));
  return r.map((row) => ({
    level: row.level,
    label: row.label ?? "",
    criteria: row.criteria ?? "",
  }));
}

function RubricFields({
  value,
  onChange,
  disabled,
}: {
  value: RubricRow[];
  onChange: (rows: RubricRow[]) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-2 rounded-lg border border-app-border bg-app-surface p-3 dark:border-gray-600 dark:bg-gray-900/50">
      <p className="text-xs font-semibold text-app-text-muted dark:text-gray-400">Rubric levels</p>
      {value.map((row, idx) => (
        <div key={row.level} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-center text-sm">
          <span className="sm:col-span-1 font-medium text-app-text-muted dark:text-gray-400">L{row.level}</span>
          <input
            type="text"
            className="sm:col-span-3 rounded border border-app-border bg-app-surface px-2 py-1.5 text-app-text placeholder:text-app-text-muted dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
            placeholder="Label"
            value={row.label}
            disabled={disabled}
            onChange={(e) => {
              const next = [...value];
              next[idx] = { ...next[idx], label: e.target.value };
              onChange(next);
            }}
          />
          <input
            type="text"
            className="sm:col-span-8 rounded border border-app-border bg-app-surface px-2 py-1.5 text-app-text placeholder:text-app-text-muted dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
            placeholder="Criteria"
            value={row.criteria}
            disabled={disabled}
            onChange={(e) => {
              const next = [...value];
              next[idx] = { ...next[idx], criteria: e.target.value };
              onChange(next);
            }}
          />
        </div>
      ))}
    </div>
  );
}

export default function LessonCanDoPage() {
  const [tab, setTab] = useState<TabKey>("lesson");
  const [rosterLoading, setRosterLoading] = useState(true);
  const [gradesFromFallback, setGradesFromFallback] = useState(false);
  const [teacherGrades, setTeacherGrades] = useState<number[]>([]);
  const [gradeFilter, setGradeFilter] = useState<number | null>(null);

  /* --- Lesson tab --- */
  const [lessons, setLessons] = useState<LessonItem[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<LessonItem | null>(null);
  const [lessonCanDos, setLessonCanDos] = useState<CanDoItem[]>([]);

  /* --- Communication tab --- */
  const [commTopics, setCommTopics] = useState<CommunicationTopicMeta[]>([]);
  const [commCanDos, setCommCanDos] = useState<CanDoItem[]>([]);
  const [selectedCommTopicId, setSelectedCommTopicId] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CanDoCreatePayload>({
    statement_ta: "",
    statement_en: "",
    rubric_levels: DEFAULT_RUBRIC.map((x) => ({ ...x })),
  });
  const [editForm, setEditForm] = useState<CanDoCreatePayload | null>(null);

  const [newTopicEn, setNewTopicEn] = useState("");
  const [newTopicTa, setNewTopicTa] = useState("");
  const [editingTopicId, setEditingTopicId] = useState<string | null>(null);
  const [editTopicEn, setEditTopicEn] = useState("");
  const [editTopicTa, setEditTopicTa] = useState("");

  useEffect(() => {
    let cancelled = false;
    setRosterLoading(true);
    setError(null);
    Promise.all([
      getTeacherMySections({ limit: 500 }).catch(() => [] as Awaited<ReturnType<typeof getTeacherMySections>>),
      getTeacherMyClasses().catch(() => [] as TeacherClass[]),
    ])
      .then(([sectionItems, classes]) => {
        if (cancelled) return;
        const merged = uniqueGradesFromRoster(sectionItems, classes);
        if (merged.length > 0) {
          setTeacherGrades(merged);
          setGradesFromFallback(false);
        } else {
          setTeacherGrades(FALLBACK_GRADES);
          setGradesFromFallback(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTeacherGrades(FALLBACK_GRADES);
          setGradesFromFallback(true);
          setError("Could not load your classes from roster. You can still pick any grade below.");
        }
      })
      .finally(() => {
        if (!cancelled) setRosterLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (teacherGrades.length === 0) {
      setGradeFilter(null);
      return;
    }
    setGradeFilter((prev) =>
      prev != null && teacherGrades.includes(prev) ? prev : teacherGrades[0]
    );
  }, [teacherGrades]);

  const loadLessons = useCallback(async () => {
    if (gradeFilter == null) {
      setLessons([]);
      setSelectedLesson(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const list = await listTeacherLessons(gradeFilter);
      setLessons(list);
      setSelectedLesson((prev) => {
        if (prev && list.some((l) => l.lesson_no === prev.lesson_no)) return prev;
        return list[0] ?? null;
      });
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e !== null && "response" in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail)
          : e instanceof Error
            ? e.message
            : "Failed to load lessons";
      setError(msg || "Failed to load lessons");
      setLessons([]);
      setSelectedLesson(null);
    } finally {
      setLoading(false);
    }
  }, [gradeFilter]);

  const loadLessonCanDos = useCallback(async () => {
    if (gradeFilter == null || !selectedLesson) return;
    setLoading(true);
    setError(null);
    try {
      const list = await getCanDos(gradeFilter, selectedLesson.lesson_no);
      setLessonCanDos(list);
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e !== null && "response" in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail)
          : e instanceof Error
            ? e.message
            : "Failed to load Can Do";
      setError(msg || "Failed to load Can Do");
    } finally {
      setLoading(false);
    }
  }, [selectedLesson, gradeFilter]);

  const loadCommunicationBundle = useCallback(async () => {
    if (gradeFilter == null) return;
    setLoading(true);
    setError(null);
    try {
      const { can_dos, communication_topics } = await fetchCanDosBundle(
        gradeFilter,
        COMMUNICATION_SKILLS_LESSON_NO
      );
      setCommCanDos(can_dos);
      setCommTopics(communication_topics);
      setSelectedCommTopicId((prev) =>
        prev && communication_topics.some((t) => t.id === prev)
          ? prev
          : communication_topics[0]?.id ?? null
      );
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e !== null && "response" in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail)
          : e instanceof Error
            ? e.message
            : "Failed to load communication topics";
      setError(msg || "Failed to load communication topics");
    } finally {
      setLoading(false);
    }
  }, [gradeFilter]);

  useEffect(() => {
    if (tab !== "lesson") return;
    void loadLessons();
  }, [tab, loadLessons]);

  useEffect(() => {
    if (tab !== "lesson") return;
    void loadLessonCanDos();
  }, [tab, loadLessonCanDos]);

  useEffect(() => {
    if (tab !== "communication") return;
    void loadCommunicationBundle();
  }, [tab, loadCommunicationBundle]);

  const gradeReady = gradeFilter != null;

  const resetAddForm = () => {
    setForm({
      statement_ta: "",
      statement_en: "",
      rubric_levels: DEFAULT_RUBRIC.map((x) => ({ ...x })),
    });
  };

  const handleGenerate = async () => {
    if (gradeFilter == null || tab !== "lesson" || !selectedLesson) return;
    setGenerating(true);
    setError(null);
    try {
      const generated = await generateCanDos(gradeFilter, selectedLesson.lesson_no);
      setLessonCanDos(generated);
      setShowAddForm(false);
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e !== null && "response" in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail)
          : e instanceof Error
            ? e.message
            : "Generation failed";
      setError(msg || "Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleCreate = async () => {
    if (gradeFilter == null || !form.statement_ta.trim() || !form.statement_en.trim()) return;
    if (tab === "communication" && !selectedCommTopicId) {
      setError("Select a communication topic first (or create one).");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload: CanDoCreatePayload =
        tab === "communication" && selectedCommTopicId
          ? { ...form, communication_topic_id: selectedCommTopicId }
          : form;
      const ln = tab === "lesson" ? selectedLesson!.lesson_no : COMMUNICATION_SKILLS_LESSON_NO;
      const created = await createCanDo(gradeFilter, ln, payload);
      if (tab === "lesson") setLessonCanDos((prev) => [...prev, created]);
      else setCommCanDos((prev) => [...prev, created]);
      resetAddForm();
      setShowAddForm(false);
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e !== null && "response" in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail)
          : e instanceof Error
            ? e.message
            : "Create failed";
      setError(msg || "Create failed");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id: string, payload: Partial<CanDoCreatePayload>) => {
    if (gradeFilter == null) return;
    const ln =
      tab === "lesson" ? selectedLesson?.lesson_no ?? null : COMMUNICATION_SKILLS_LESSON_NO;
    if (ln == null) return;
    setLoading(true);
    setError(null);
    try {
      const updated = await updateCanDo(gradeFilter, ln, id, payload);
      if (tab === "lesson")
        setLessonCanDos((prev) => prev.map((c) => (c.id === id ? updated : c)));
      else setCommCanDos((prev) => prev.map((c) => (c.id === id ? updated : c)));
      setEditingId(null);
      setEditForm(null);
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e !== null && "response" in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail)
          : e instanceof Error
            ? e.message
            : "Update failed";
      setError(msg || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (gradeFilter == null || !window.confirm("Delete this Can Do?")) return;
    const ln =
      tab === "lesson" ? selectedLesson?.lesson_no ?? null : COMMUNICATION_SKILLS_LESSON_NO;
    if (ln == null) return;
    setLoading(true);
    setError(null);
    try {
      await deleteCanDo(gradeFilter, ln, id);
      if (tab === "lesson") setLessonCanDos((prev) => prev.filter((c) => c.id !== id));
      else setCommCanDos((prev) => prev.filter((c) => c.id !== id));
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e !== null && "response" in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail)
          : e instanceof Error
            ? e.message
            : "Delete failed";
      setError(msg || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (cd: CanDoItem) => {
    setEditingId(cd.id);
    setEditForm({
      statement_ta: cd.statement_ta,
      statement_en: cd.statement_en,
      rubric_levels: normalizeRubric(cd.rubric_levels),
      ...(tab === "communication" && cd.communication_topic_id
        ? { communication_topic_id: cd.communication_topic_id }
        : {}),
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm(null);
  };

  const handleCreateTopic = async () => {
    if (gradeFilter == null || !newTopicEn.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const topic = await createCommunicationTopic(gradeFilter, {
        title_en: newTopicEn.trim(),
        title_ta: newTopicTa.trim() || undefined,
      });
      setCommTopics((prev) => [...prev, topic]);
      setSelectedCommTopicId(topic.id);
      setNewTopicEn("");
      setNewTopicTa("");
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e !== null && "response" in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail)
          : e instanceof Error
            ? e.message
            : "Failed to create topic";
      setError(msg || "Failed to create topic");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTopicEdit = async () => {
    if (gradeFilter == null || !editingTopicId || !editTopicEn.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const updated = await updateCommunicationTopic(gradeFilter, editingTopicId, {
        title_en: editTopicEn.trim(),
        title_ta: editTopicTa.trim() || undefined,
      });
      setCommTopics((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
      setEditingTopicId(null);
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e !== null && "response" in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail)
          : e instanceof Error
            ? e.message
            : "Update failed";
      setError(msg || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTopic = async (topicId: string) => {
    if (gradeFilter == null || !window.confirm("Delete this topic and all can-dos under it?")) return;
    setLoading(true);
    setError(null);
    try {
      await deleteCommunicationTopic(gradeFilter, topicId);
      await loadCommunicationBundle();
      if (selectedCommTopicId === topicId) setSelectedCommTopicId(null);
    } catch (e: unknown) {
      const msg =
        typeof e === "object" && e !== null && "response" in e
          ? String((e as { response?: { data?: { detail?: string } } }).response?.data?.detail)
          : e instanceof Error
            ? e.message
            : "Delete failed";
      setError(msg || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const commCanDosForTopic = commCanDos.filter((c) => c.communication_topic_id === selectedCommTopicId);
  const displayCanDos = tab === "lesson" ? lessonCanDos : commCanDosForTopic;

  return (
    <Layout>
      <PageHeader
        title="Can Do"
        subtitle="Lesson outcomes for the AI tutor, and communication topics + skills for the Communication Tutor."
      />

      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200">
            {error}
          </div>
        )}

        {rosterLoading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-app-text-muted dark:text-gray-400">
            <Spinner />
            <span>Loading your classes…</span>
          </div>
        ) : (
          <>
            {gradesFromFallback && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                No grade was found on your assigned classes or sections in roster, or roster could not be loaded.
                Sections are optional — choose any grade below.
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4">
              <label className="text-sm font-medium text-app-text dark:text-gray-200">
                Grade
                <select
                  className="ml-2 rounded-lg border border-app-border bg-app-surface px-2 py-1 text-sm text-app-text dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                  value={gradeFilter ?? ""}
                  onChange={(e) => setGradeFilter(Number(e.target.value))}
                >
                  {teacherGrades.map((g) => (
                    <option key={g} value={g}>
                      {g}
                    </option>
                  ))}
                </select>
              </label>

              <div className="flex rounded-xl border border-app-border bg-app-surface-muted/80 p-0.5 dark:border-gray-600 dark:bg-gray-800/80">
                <button
                  type="button"
                  onClick={() => setTab("lesson")}
                  className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    tab === "lesson"
                      ? "bg-white text-indigo-700 shadow dark:bg-gray-700 dark:text-indigo-300"
                      : "text-app-text-muted dark:text-gray-400"
                  }`}
                >
                  <BookOpen size={16} />
                  Lesson Can-Do
                </button>
                <button
                  type="button"
                  onClick={() => setTab("communication")}
                  className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    tab === "communication"
                      ? "bg-white text-indigo-700 shadow dark:bg-gray-700 dark:text-indigo-300"
                      : "text-app-text-muted dark:text-gray-400"
                  }`}
                >
                  <MessageCircle size={16} />
                  Communication skills
                </button>
              </div>
            </div>

            {tab === "lesson" && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                      <BookOpen size={18} />
                      Lessons
                      {gradeReady ? (
                        <span className="text-sm font-normal text-app-text-muted dark:text-gray-400">
                          (grade {gradeFilter})
                        </span>
                      ) : null}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loading && !lessons.length ? (
                      <p className="text-sm text-app-text-muted dark:text-gray-400">Loading...</p>
                    ) : lessons.length === 0 ? (
                      <p className="text-sm text-app-text-muted dark:text-gray-400">
                        No lessons found for this grade in the content index.
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {lessons.map((l) => (
                          <button
                            key={l.lesson_no}
                            type="button"
                            onClick={() => setSelectedLesson(l)}
                            className={`w-full rounded-xl border px-4 py-3 text-left transition ${
                              selectedLesson?.lesson_no === l.lesson_no
                                ? "border-indigo-300 bg-indigo-50 dark:border-indigo-600 dark:bg-indigo-950/40"
                                : "border-app-border bg-app-surface hover:bg-app-surface-muted dark:border-gray-600 dark:bg-gray-900/40 dark:hover:bg-gray-800"
                            }`}
                          >
                            <div className="font-semibold text-app-text dark:text-white">
                              {l.lesson_no}. {l.lesson_title}
                            </div>
                            {l.lesson_type && (
                              <div className="mt-1 text-xs text-app-text-muted dark:text-gray-400">{l.lesson_type}</div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                <div className="lg:col-span-2">
                  <Card>
                    <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
                      <CardTitle>
                        {selectedLesson
                          ? `Can Do — Lesson ${selectedLesson.lesson_no}`
                          : "Select a lesson"}
                      </CardTitle>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleGenerate}
                          disabled={!selectedLesson || !gradeReady || generating}
                        >
                          <Sparkles size={16} className="mr-1" />
                          {generating ? "Generating..." : "Generate"}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            resetAddForm();
                            setShowAddForm(true);
                          }}
                          disabled={!selectedLesson || !gradeReady}
                        >
                          <Plus size={16} className="mr-1" />
                          Add
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {renderCanDoList({
                        loading,
                        showAddForm,
                        setShowAddForm,
                        form,
                        setForm,
                        editForm,
                        setEditForm,
                        editingId,
                        canDos: lessonCanDos,
                        gradeReady,
                        selectedLesson: !!selectedLesson,
                        resetAddForm,
                        handleCreate,
                        handleUpdate,
                        handleDelete,
                        startEdit,
                        cancelEdit,
                        emptyMessage: "No Can Do statements for this lesson yet.",
                        tab: "lesson",
                      })}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {tab === "communication" && (
              <div className="grid grid-cols-1 gap-6">
                <p className="text-sm text-app-text-muted dark:text-gray-400 -mt-2">
                  When topics and can-dos are ready, add due-dated practice tasks under{" "}
                  <Link to={ROUTES.TEACHER_ASSIGNMENTS_COMM_TASKS} className="font-semibold text-indigo-600 underline">
                    Assignments → Communication
                  </Link>
                  . Students see those on My Assignments and here while the task is open.
                </p>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <MessageCircle size={18} />
                      Topics
                    </CardTitle>
                    <p className="mt-1 text-xs text-app-text-muted dark:text-gray-400">
                      Each topic appears as one row in the Communication Tutor. Add can-dos under a topic for tagging
                      and rubrics.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2 rounded-xl border border-app-border bg-app-surface-muted/50 p-3 dark:border-gray-600 dark:bg-gray-800/50">
                      <p className="text-xs font-semibold text-app-text-muted dark:text-gray-300">New topic</p>
                      <input
                        className="w-full rounded border border-app-border bg-app-surface px-2 py-1.5 text-sm text-app-text placeholder:text-app-text-muted dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
                        placeholder="Topic title (English)"
                        value={newTopicEn}
                        onChange={(e) => setNewTopicEn(e.target.value)}
                      />
                      <input
                        className="w-full rounded border border-app-border bg-app-surface px-2 py-1.5 text-sm text-app-text placeholder:text-app-text-muted dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
                        placeholder="Topic title (Tamil, optional)"
                        value={newTopicTa}
                        onChange={(e) => setNewTopicTa(e.target.value)}
                      />
                      <Button size="sm" onClick={handleCreateTopic} disabled={!gradeReady || loading}>
                        Add topic
                      </Button>
                    </div>
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {commTopics.length === 0 ? (
                        <p className="text-sm text-app-text-muted dark:text-gray-400">No topics yet. Create one above.</p>
                      ) : (
                        commTopics.map((t) => (
                          <div
                            key={t.id}
                            className="overflow-hidden rounded-xl border border-app-border dark:border-gray-600"
                          >
                            {editingTopicId === t.id ? (
                              <div className="space-y-2 bg-amber-50/50 p-3 dark:bg-amber-950/20">
                                <input
                                  className="w-full rounded border border-app-border bg-app-surface px-2 py-1 text-sm text-app-text dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                  value={editTopicEn}
                                  onChange={(e) => setEditTopicEn(e.target.value)}
                                />
                                <input
                                  className="w-full rounded border border-app-border bg-app-surface px-2 py-1 text-sm text-app-text dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                  value={editTopicTa}
                                  onChange={(e) => setEditTopicTa(e.target.value)}
                                />
                                <div className="flex gap-2">
                                  <Button size="sm" onClick={handleSaveTopicEdit}>
                                    Save
                                  </Button>
                                  <Button variant="outline" size="sm" onClick={() => setEditingTopicId(null)}>
                                    Cancel
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start gap-2 p-3">
                                <button
                                  type="button"
                                  onClick={() => setSelectedCommTopicId(t.id)}
                                  className={`flex-1 rounded-lg px-2 py-1 text-left transition ${
                                    selectedCommTopicId === t.id
                                      ? "bg-indigo-100 text-indigo-900 dark:bg-indigo-950/50 dark:text-indigo-100"
                                      : "hover:bg-app-surface-muted dark:hover:bg-gray-800"
                                  }`}
                                >
                                  <div className="font-semibold text-app-text dark:text-white">{t.title_en}</div>
                                  {t.title_ta ? (
                                    <div className="mt-0.5 text-xs text-app-text-muted dark:text-gray-400">
                                      {t.title_ta}
                                    </div>
                                  ) : null}
                                </button>
                                <div className="flex shrink-0 flex-col gap-1">
                                  <button
                                    type="button"
                                    className="rounded p-1.5 hover:bg-app-surface-muted dark:hover:bg-gray-700"
                                    title="Edit topic"
                                    onClick={() => {
                                      setEditingTopicId(t.id);
                                      setEditTopicEn(t.title_en);
                                      setEditTopicTa(t.title_ta || "");
                                    }}
                                  >
                                    <Pencil size={14} />
                                  </button>
                                  <button
                                    type="button"
                                    className="rounded p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                                    title="Delete topic"
                                    onClick={() => handleDeleteTopic(t.id)}
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>

                <div className="lg:col-span-2">
                  <Card>
                    <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
                      <CardTitle>
                        {selectedCommTopicId
                          ? `Can-Dos — ${commTopics.find((x) => x.id === selectedCommTopicId)?.title_en ?? "Topic"}`
                          : "Select a topic"}
                      </CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          resetAddForm();
                          setShowAddForm(true);
                        }}
                        disabled={!selectedCommTopicId || !gradeReady}
                      >
                        <Plus size={16} className="mr-1" />
                        Add can-do
                      </Button>
                    </CardHeader>
                    <CardContent>
                      {renderCanDoList({
                        loading,
                        showAddForm,
                        setShowAddForm,
                        form,
                        setForm,
                        editForm,
                        setEditForm,
                        editingId,
                        canDos: displayCanDos,
                        gradeReady,
                        selectedLesson: !!selectedCommTopicId,
                        resetAddForm,
                        handleCreate,
                        handleUpdate,
                        handleDelete,
                        startEdit,
                        cancelEdit,
                        emptyMessage: "No can-dos under this topic yet.",
                        tab: "communication",
                      })}
                    </CardContent>
                  </Card>
                </div>
              </div>
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
}

function renderCanDoList(props: {
  loading: boolean;
  showAddForm: boolean;
  setShowAddForm: (v: boolean) => void;
  form: CanDoCreatePayload;
  setForm: React.Dispatch<React.SetStateAction<CanDoCreatePayload>>;
  editForm: CanDoCreatePayload | null;
  setEditForm: React.Dispatch<React.SetStateAction<CanDoCreatePayload | null>>;
  editingId: string | null;
  canDos: CanDoItem[];
  gradeReady: boolean;
  selectedLesson: boolean;
  resetAddForm: () => void;
  handleCreate: () => void | Promise<void>;
  handleUpdate: (id: string, p: Partial<CanDoCreatePayload>) => void | Promise<void>;
  handleDelete: (id: string) => void | Promise<void>;
  startEdit: (cd: CanDoItem) => void;
  cancelEdit: () => void;
  emptyMessage: string;
  tab: TabKey;
}) {
  const {
    loading,
    showAddForm,
    setShowAddForm,
    form,
    setForm,
    editForm,
    setEditForm,
    editingId,
    canDos,
    gradeReady,
    selectedLesson,
    resetAddForm,
    handleCreate,
    handleUpdate,
    handleDelete,
    startEdit,
    cancelEdit,
    emptyMessage,
    tab,
  } = props;

  return (
    <>
      {loading && canDos.length === 0 ? (
        <p className="py-4 text-sm text-app-text-muted dark:text-gray-400">Loading…</p>
      ) : null}

      {showAddForm && (
        <div className="mb-6 space-y-3 rounded-xl border border-app-border bg-app-surface-muted/50 p-4 dark:border-gray-600 dark:bg-gray-800/50">
          <input
            type="text"
            placeholder="Tamil statement (e.g. நான் வணக்கம் சொல்ல முடியும்)"
            className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-text placeholder:text-app-text-muted dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
            value={form.statement_ta}
            onChange={(e) => setForm((f) => ({ ...f, statement_ta: e.target.value }))}
          />
          <input
            type="text"
            placeholder="English statement (e.g. I can greet)"
            className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-text placeholder:text-app-text-muted dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
            value={form.statement_en}
            onChange={(e) => setForm((f) => ({ ...f, statement_en: e.target.value }))}
          />
          <RubricFields
            value={(form.rubric_levels ?? DEFAULT_RUBRIC) as RubricRow[]}
            onChange={(rows) => setForm((f) => ({ ...f, rubric_levels: rows }))}
          />
          {tab === "communication" && (
            <p className="text-xs text-app-text-muted dark:text-gray-400">
              This can-do is saved under the selected communication topic.
            </p>
          )}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void handleCreate()} disabled={loading || !gradeReady || !selectedLesson}>
              Save
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowAddForm(false);
                resetAddForm();
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {!loading && canDos.length === 0 && !showAddForm ? (
          <p className="py-2 text-sm text-app-text-muted dark:text-gray-400">{emptyMessage}</p>
        ) : null}
        {canDos.map((cd) => (
          <div
            key={cd.id}
            className="flex items-start justify-between gap-4 rounded-xl border border-app-border bg-app-surface p-4 dark:border-gray-600 dark:bg-gray-900/40"
          >
            <div className="flex-1 min-w-0 space-y-2">
              {editingId === cd.id && editForm ? (
                <>
                  <input
                    type="text"
                    className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-text dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    value={editForm.statement_ta}
                    onChange={(e) => setEditForm((f) => (f ? { ...f, statement_ta: e.target.value } : f))}
                  />
                  <input
                    type="text"
                    className="w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-text dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                    value={editForm.statement_en}
                    onChange={(e) => setEditForm((f) => (f ? { ...f, statement_en: e.target.value } : f))}
                  />
                  <RubricFields
                    value={normalizeRubric(editForm.rubric_levels)}
                    onChange={(rows) => setEditForm((f) => (f ? { ...f, rubric_levels: rows } : f))}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        if (!editForm.statement_ta.trim() || !editForm.statement_en.trim()) return;
                        void handleUpdate(cd.id, {
                          statement_ta: editForm.statement_ta,
                          statement_en: editForm.statement_en,
                          rubric_levels: editForm.rubric_levels,
                          ...(tab === "communication" && editForm.communication_topic_id
                            ? { communication_topic_id: editForm.communication_topic_id }
                            : {}),
                        });
                      }}
                    >
                      Save
                    </Button>
                    <Button variant="outline" size="sm" onClick={cancelEdit}>
                      Cancel
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-medium text-app-text dark:text-white">{cd.statement_ta}</p>
                  <p className="text-sm text-app-text-muted dark:text-gray-300">{cd.statement_en}</p>
                  {cd.rubric_levels && cd.rubric_levels.length > 0 && (
                    <ul className="list-disc space-y-0.5 pl-4 text-xs text-app-text-muted dark:text-gray-400">
                      {cd.rubric_levels.map((r) => (
                        <li key={r.level}>
                          <span className="font-medium text-app-text dark:text-gray-200">
                            L{r.level} {r.label}:
                          </span>{" "}
                          {r.criteria}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
            {editingId !== cd.id && (
              <div className="flex gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => startEdit(cd)}
                  className="rounded-lg p-2 text-app-text-muted hover:bg-app-surface-muted dark:text-gray-400 dark:hover:bg-gray-800"
                  title="Edit"
                >
                  <Pencil size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => void handleDelete(cd.id)}
                  className="rounded-lg p-2 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
